import { useEffect, useState } from "react";
import {
    richTextToPlain,
    walkNodes,
    type EmailDocument,
    type RichDoc,
    type StyleProps,
} from "@edm/schema";
import { fetchCapabilities } from "../../api/capabilities";
import { AiProposalDialog, type AssistRequest } from "../ai/AiProposalDialog";

function documentPlainText(doc: EmailDocument): string {
    const parts: string[] = [];
    walkNodes(doc.root, (n) => {
        const content = n.attrs.content as RichDoc | undefined;
        if (content?.type === "doc") parts.push(richTextToPlain(content));
    });
    return parts.join("\n").slice(0, 4000);
}
import { useEditorStore } from "../store/editorStore";
import { getNode, updateAttrs, updateStyles } from "../docops";
import { effectiveStyles } from "../canvas/effectiveStyles";
import { api } from "../../api/client";
import { MediaManager } from "../media/MediaManager";
import {
    AlignField,
    ColorField,
    Field,
    NumberField,
    PaddingFields,
    SelectField,
    TextField,
} from "./fields";

interface BrandKitOption {
    id: string;
    name: string;
    isDefault: boolean;
}

export function InspectorPanel({ document: doc }: { document: EmailDocument }) {
    const selectedId = useEditorStore((s) => s.selectedId);
    const applyDocChange = useEditorStore((s) => s.applyDocChange);
    const [tab, setTab] = useState<"desktop" | "mobile">("desktop");
    const [brandKits, setBrandKits] = useState<BrandKitOption[]>([]);
    const [pickingImage, setPickingImage] = useState(false);
    const [pickingHeroBg, setPickingHeroBg] = useState(false);
    const [aiEnabled, setAiEnabled] = useState(false);
    const [aiRequest, setAiRequest] = useState<AssistRequest | null>(null);
    const [aiTarget, setAiTarget] = useState<"subject" | "preheader">("subject");

    useEffect(() => {
        api.get<BrandKitOption[]>("/api/v1/brand-kits")
            .then(setBrandKits)
            .catch(() => setBrandKits([]));
        void fetchCapabilities().then((c) => setAiEnabled(c.ai));
    }, []);

    const node = selectedId ? getNode(doc.root, selectedId) : null;
    const mobile = tab === "mobile";

    const setStyle = (patch: StyleProps) =>
        applyDocChange((d) => updateStyles(d, selectedId!, patch, mobile), {
            coalesceKey: `style:${tab}:${selectedId}`,
        });
    const setAttr = (attrs: Record<string, unknown>) =>
        applyDocChange((d) => updateAttrs(d, selectedId!, attrs), {
            coalesceKey: `attr:${selectedId}`,
        });
    const setMeta = (patch: Partial<EmailDocument["meta"]>) =>
        applyDocChange(
            (d) => {
                Object.assign(d.meta, patch);
            },
            { coalesceKey: "meta" },
        );
    const setSettings = (patch: Partial<EmailDocument["settings"]>) =>
        applyDocChange(
            (d) => {
                Object.assign(d.settings, patch);
            },
            { coalesceKey: "settings" },
        );

    if (!node) {
        return (
            <aside className="inspector" aria-label="Email settings" data-testid="inspector">
                <h3 className="insp-heading">Email settings</h3>
                <TextField
                    label="Subject"
                    value={doc.meta.subject}
                    onChange={(v) => setMeta({ subject: v })}
                    placeholder="Subject line"
                />
                {aiEnabled && (
                    <button
                        type="button"
                        className="tb-btn insp-wide"
                        data-testid="ai-subject-lines"
                        onClick={() => {
                            setAiTarget("subject");
                            setAiRequest({
                                action: "subject-lines",
                                text: doc.meta.subject,
                                context: {
                                    subject: doc.meta.subject,
                                    bodyText: documentPlainText(doc),
                                },
                            });
                        }}
                    >
                        ✨ Suggest subject lines
                    </button>
                )}
                <TextField
                    label="Preheader"
                    value={doc.meta.preheader}
                    onChange={(v) => setMeta({ preheader: v })}
                    placeholder="Hidden preview text"
                />
                {aiEnabled && (
                    <button
                        type="button"
                        className="tb-btn insp-wide"
                        onClick={() => {
                            setAiTarget("preheader");
                            setAiRequest({
                                action: "preheaders",
                                text: doc.meta.preheader,
                                context: {
                                    subject: doc.meta.subject,
                                    bodyText: documentPlainText(doc),
                                },
                            });
                        }}
                    >
                        ✨ Suggest preheaders
                    </button>
                )}
                {aiRequest && (
                    <AiProposalDialog
                        request={aiRequest}
                        onClose={() => setAiRequest(null)}
                        onAccept={(text) =>
                            setMeta(aiTarget === "subject" ? { subject: text } : { preheader: text })
                        }
                    />
                )}
                <ColorField
                    label="Background"
                    value={doc.settings.backgroundColor}
                    onChange={(v) => setSettings({ backgroundColor: v })}
                />
                <NumberField
                    label="Content width"
                    value={doc.settings.contentWidth}
                    min={320}
                    max={900}
                    onChange={(v) => v !== undefined && setSettings({ contentWidth: v })}
                />
                <SelectField
                    label="Brand kit"
                    value={doc.settings.brandKitId ?? ""}
                    options={[
                        { value: "", label: "Tenant default" },
                        ...brandKits.map((k) => ({
                            value: k.id,
                            label: k.isDefault ? `${k.name} (default)` : k.name,
                        })),
                    ]}
                    onChange={(v) =>
                        applyDocChange((d) => {
                            if (v) d.settings.brandKitId = v;
                            else delete d.settings.brandKitId;
                        })
                    }
                />
            </aside>
        );
    }

    const styles = mobile ? node.mobileStyles : node.styles;
    const eff = effectiveStyles(doc, node);

    if (mobile) {
        return (
            <aside className="inspector" aria-label="Block settings" data-testid="inspector">
                <h3 className="insp-heading">{node.type}</h3>
                <StyleTabs tab={tab} setTab={setTab} />
                <p className="insp-hint">
                    Mobile overrides apply below 480px. Empty fields inherit desktop values.
                </p>
                <NumberField
                    label="Font size"
                    value={styles.fontSize}
                    min={8}
                    max={96}
                    unset
                    onChange={(v) => setStyle({ fontSize: v })}
                />
                <AlignField
                    label="Text align"
                    value={styles.textAlign}
                    onChange={(v) => setStyle({ textAlign: v })}
                />
                <NumberField
                    label="Height"
                    value={styles.height}
                    min={0}
                    max={800}
                    unset
                    onChange={(v) => setStyle({ height: v })}
                />
                <PaddingFields values={styles} onChange={(key, v) => setStyle({ [key]: v })} />
                <VisibilityToggles
                    values={effectiveStyles(doc, node)}
                    onChange={(patch) =>
                        applyDocChange((d) => updateStyles(d, selectedId!, patch, false), {
                            coalesceKey: `style:vis:${selectedId}`,
                        })
                    }
                />
            </aside>
        );
    }

    return (
        <aside className="inspector" aria-label="Block settings" data-testid="inspector">
            <h3 className="insp-heading">{node.type}</h3>
            <StyleTabs tab={tab} setTab={setTab} />

            {node.type === "heading" && (
                <SelectField
                    label="Level"
                    value={String(node.attrs.level ?? 2) as "1"}
                    options={[
                        { value: "1", label: "H1" },
                        { value: "2", label: "H2" },
                        { value: "3", label: "H3" },
                    ]}
                    onChange={(v) => setAttr({ level: Number(v) })}
                />
            )}

            {(node.type === "image" || node.type === "logo") && (
                <>
                    <button
                        type="button"
                        className="tb-btn insp-wide"
                        onClick={() => setPickingImage(true)}
                        data-testid="choose-image"
                    >
                        Choose from library…
                    </button>
                    {pickingImage && (
                        <MediaManager
                            onClose={() => setPickingImage(false)}
                            onPick={(item, backend) => {
                                setPickingImage(false);
                                applyDocChange((d) =>
                                    updateAttrs(d, selectedId!, {
                                        src: item.url ?? "",
                                        // Only editor-own assets carry an assetId the
                                        // validators can resolve; do-core-hosted images
                                        // are referenced by their public URL alone.
                                        assetId: backend === "own" ? item.id : undefined,
                                    }),
                                );
                            }}
                        />
                    )}
                </>
            )}

            {node.type === "image" && (
                <>
                    <TextField
                        label="Image URL"
                        value={(node.attrs.src as string) ?? ""}
                        onChange={(v) => setAttr({ src: v })}
                        placeholder="https://…"
                    />
                    <TextField
                        label="Alt text"
                        value={(node.attrs.alt as string) ?? ""}
                        onChange={(v) => setAttr({ alt: v })}
                    />
                    <TextField
                        label="Link"
                        value={(node.attrs.href as string) ?? ""}
                        onChange={(v) => setAttr({ href: v })}
                        placeholder="https://…"
                    />
                    <NumberField
                        label="Width (px)"
                        value={styles.width}
                        unset
                        onChange={(v) => setStyle({ width: v })}
                    />
                    <AlignField
                        label="Align"
                        value={eff.align}
                        onChange={(v) => setStyle({ align: v as "left" })}
                    />
                </>
            )}

            {node.type === "button" && (
                <>
                    <TextField
                        label="Label"
                        value={(node.attrs.text as string) ?? ""}
                        onChange={(v) => setAttr({ text: v })}
                    />
                    <TextField
                        label="Link"
                        value={(node.attrs.href as string) ?? ""}
                        onChange={(v) => setAttr({ href: v })}
                        placeholder="https://…"
                    />
                    <ColorField
                        label="Background"
                        value={eff.backgroundColor}
                        onChange={(v) => setStyle({ backgroundColor: v })}
                    />
                    <ColorField
                        label="Text colour"
                        value={eff.color}
                        onChange={(v) => setStyle({ color: v })}
                    />
                    <NumberField
                        label="Corner radius"
                        value={eff.borderRadius}
                        max={60}
                        onChange={(v) => setStyle({ borderRadius: v })}
                    />
                    <AlignField
                        label="Align"
                        value={eff.align}
                        onChange={(v) => setStyle({ align: v as "left" })}
                    />
                </>
            )}

            {(node.type === "heading" || node.type === "paragraph") && (
                <>
                    <NumberField
                        label="Font size"
                        value={eff.fontSize}
                        min={8}
                        max={96}
                        onChange={(v) => setStyle({ fontSize: v })}
                    />
                    <ColorField
                        label="Colour"
                        value={eff.color}
                        onChange={(v) => setStyle({ color: v })}
                    />
                    <AlignField
                        label="Align"
                        value={eff.textAlign}
                        allowJustify
                        onChange={(v) => setStyle({ textAlign: v })}
                    />
                </>
            )}

            {node.type === "section" && (
                <ColorField
                    label="Background"
                    value={eff.backgroundColor}
                    onChange={(v) => setStyle({ backgroundColor: v })}
                />
            )}

            {node.type === "spacer" && (
                <NumberField
                    label="Height"
                    value={eff.height}
                    min={4}
                    max={300}
                    onChange={(v) => setStyle({ height: v })}
                />
            )}

            {node.type === "divider" && (
                <>
                    <ColorField
                        label="Line colour"
                        value={eff.borderColor}
                        onChange={(v) => setStyle({ borderColor: v })}
                    />
                    <NumberField
                        label="Thickness"
                        value={eff.borderWidth}
                        min={1}
                        max={20}
                        onChange={(v) => setStyle({ borderWidth: v })}
                    />
                </>
            )}

            {node.type === "hero" && (
                <>
                    <button
                        type="button"
                        className="tb-btn insp-wide"
                        data-testid="choose-hero-bg"
                        onClick={() => setPickingHeroBg(true)}
                    >
                        Choose background from library…
                    </button>
                    {pickingHeroBg && (
                        <MediaManager
                            onClose={() => setPickingHeroBg(false)}
                            onPick={(item) => {
                                setPickingHeroBg(false);
                                // Hero's schema is strict to { src, height } — no
                                // assetId here (unlike image/logo), or save/validate
                                // would reject the block.
                                applyDocChange((d) =>
                                    updateAttrs(d, selectedId!, { src: item.url ?? "" }),
                                );
                            }}
                        />
                    )}
                    <TextField
                        label="Background image URL"
                        value={(node.attrs.src as string) ?? ""}
                        onChange={(v) => setAttr({ src: v })}
                        placeholder="https://… or pick from library"
                    />
                    {(node.attrs.src as string) && (
                        <button
                            type="button"
                            className="tb-btn insp-wide"
                            onClick={() => setAttr({ src: "" })}
                        >
                            Remove background image
                        </button>
                    )}
                    <ColorField
                        label="Background colour"
                        value={eff.backgroundColor}
                        onChange={(v) => setStyle({ backgroundColor: v })}
                    />
                </>
            )}

            {node.type === "card" && (
                <>
                    <ColorField
                        label="Background"
                        value={eff.backgroundColor}
                        onChange={(v) => setStyle({ backgroundColor: v })}
                    />
                    <ColorField
                        label="Border colour"
                        value={eff.borderColor}
                        onChange={(v) => setStyle({ borderColor: v })}
                    />
                    <NumberField
                        label="Corner radius"
                        value={eff.borderRadius}
                        max={60}
                        onChange={(v) => setStyle({ borderRadius: v })}
                    />
                </>
            )}

            {node.type === "logo" && (
                <>
                    <TextField
                        label="Logo URL"
                        value={(node.attrs.src as string) ?? ""}
                        onChange={(v) => setAttr({ src: v })}
                        placeholder="https://…"
                    />
                    <TextField
                        label="Alt text"
                        value={(node.attrs.alt as string) ?? ""}
                        onChange={(v) => setAttr({ alt: v })}
                    />
                    <TextField
                        label="Link"
                        value={(node.attrs.href as string) ?? ""}
                        onChange={(v) => setAttr({ href: v })}
                    />
                    <NumberField
                        label="Width (px)"
                        value={eff.width}
                        min={24}
                        max={600}
                        onChange={(v) => setStyle({ width: v })}
                    />
                    <AlignField
                        label="Align"
                        value={eff.align}
                        onChange={(v) => setStyle({ align: v as "left" })}
                    />
                </>
            )}

            {node.type === "social" && (
                <>
                    <LinkListEditor
                        label="Social links"
                        items={(node.attrs.links as { network: string; url: string }[]) ?? []}
                        keyField="network"
                        keyOptions={["facebook", "instagram", "linkedin", "x", "youtube", "tiktok", "web"]}
                        onChange={(links) => setAttr({ links })}
                    />
                    <NumberField
                        label="Icon size"
                        value={(node.attrs.iconSize as number) ?? 28}
                        min={16}
                        max={64}
                        onChange={(v) => v !== undefined && setAttr({ iconSize: v })}
                    />
                    <AlignField
                        label="Align"
                        value={eff.align}
                        onChange={(v) => setStyle({ align: v as "left" })}
                    />
                </>
            )}

            {node.type === "nav" && (
                <LinkListEditor
                    label="Menu links"
                    items={(node.attrs.links as { label: string; href: string }[]) ?? []}
                    keyField="label"
                    onChange={(links) => setAttr({ links })}
                />
            )}

            {node.type === "callout" && (
                <>
                    <ColorField
                        label="Background"
                        value={eff.backgroundColor}
                        onChange={(v) => setStyle({ backgroundColor: v })}
                    />
                    <NumberField
                        label="Corner radius"
                        value={eff.borderRadius}
                        max={60}
                        onChange={(v) => setStyle({ borderRadius: v })}
                    />
                </>
            )}

            {node.type === "quote" && (
                <>
                    <TextField
                        label="Attribution"
                        value={(node.attrs.attribution as string) ?? ""}
                        onChange={(v) => setAttr({ attribution: v })}
                        placeholder="Who said it"
                    />
                    <ColorField
                        label="Accent colour"
                        value={eff.borderColor}
                        onChange={(v) => setStyle({ borderColor: v })}
                    />
                </>
            )}

            {node.type === "footer" && (
                <>
                    <Field label="Unsubscribe link">
                        <label className="insp-check">
                            <input
                                type="checkbox"
                                checked={node.attrs.includeUnsubscribe !== false}
                                onChange={(e) => setAttr({ includeUnsubscribe: e.target.checked })}
                            />
                            Include unsubscribe link
                        </label>
                    </Field>
                    <TextField
                        label="Unsubscribe label"
                        value={(node.attrs.unsubscribeLabel as string) ?? "Unsubscribe"}
                        onChange={(v) => setAttr({ unsubscribeLabel: v })}
                    />
                </>
            )}

            {node.type === "rawHtml" && (
                <Field label="HTML (sanitised at render)">
                    <textarea
                        className="insp-input insp-textarea"
                        rows={10}
                        value={(node.attrs.html as string) ?? ""}
                        onChange={(e) => setAttr({ html: e.target.value })}
                    />
                </Field>
            )}

            {(node.type === "callout" || node.type === "quote" || node.type === "footer" || node.type === "nav") && (
                <>
                    <NumberField
                        label="Font size"
                        value={eff.fontSize}
                        min={8}
                        max={96}
                        onChange={(v) => setStyle({ fontSize: v })}
                    />
                    <ColorField
                        label="Colour"
                        value={eff.color}
                        onChange={(v) => setStyle({ color: v })}
                    />
                </>
            )}

            {node.type !== "spacer" && (
                <PaddingFields
                    values={styles}
                    onChange={(key, v) => setStyle({ [key]: v })}
                />
            )}

            <VisibilityToggles values={eff} onChange={(patch) => setStyle(patch)} />
        </aside>
    );
}

function StyleTabs({
    tab,
    setTab,
}: {
    tab: "desktop" | "mobile";
    setTab: (t: "desktop" | "mobile") => void;
}) {
    return (
        <div className="insp-tabs" role="tablist" aria-label="Style target">
            {(["desktop", "mobile"] as const).map((t) => (
                <button
                    key={t}
                    type="button"
                    role="tab"
                    aria-selected={tab === t}
                    className={`insp-tab${tab === t ? " active" : ""}`}
                    onClick={() => setTab(t)}
                >
                    {t === "desktop" ? "Desktop" : "Mobile"}
                </button>
            ))}
        </div>
    );
}

function VisibilityToggles({
    values,
    onChange,
}: {
    values: StyleProps;
    onChange: (patch: StyleProps) => void;
}) {
    return (
        <Field label="Visibility">
            <label className="insp-check">
                <input
                    type="checkbox"
                    checked={values.hideOnMobile === true}
                    onChange={(e) => onChange({ hideOnMobile: e.target.checked || undefined })}
                />
                Hide on mobile
            </label>
            <label className="insp-check">
                <input
                    type="checkbox"
                    checked={values.hideOnDesktop === true}
                    onChange={(e) => onChange({ hideOnDesktop: e.target.checked || undefined })}
                />
                Hide on desktop
            </label>
        </Field>
    );
}

function LinkListEditor<T extends Record<string, string>>({
    label,
    items,
    keyField,
    keyOptions,
    onChange,
}: {
    label: string;
    items: T[];
    keyField: string;
    keyOptions?: string[];
    onChange: (items: Record<string, string>[]) => void;
}) {
    const valueField = keyField === "network" ? "url" : "href";
    const update = (i: number, field: string, value: string) => {
        const next = items.map((item, j) => (j === i ? { ...item, [field]: value } : item));
        onChange(next);
    };
    return (
        <Field label={label}>
            <div className="link-list">
                {items.map((item, i) => (
                    <div key={i} className="link-row">
                        {keyOptions ? (
                            <select
                                className="insp-input"
                                value={item[keyField] ?? ""}
                                aria-label="Network"
                                onChange={(e) => update(i, keyField, e.target.value)}
                            >
                                {keyOptions.map((o) => (
                                    <option key={o} value={o}>
                                        {o}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <input
                                className="insp-input"
                                value={item[keyField] ?? ""}
                                placeholder="Label"
                                aria-label="Label"
                                onChange={(e) => update(i, keyField, e.target.value)}
                            />
                        )}
                        <input
                            className="insp-input"
                            value={item[valueField] ?? ""}
                            placeholder="https://…"
                            aria-label="URL"
                            onChange={(e) => update(i, valueField, e.target.value)}
                        />
                        <button
                            type="button"
                            className="tb-btn danger"
                            aria-label="Remove link"
                            onClick={() => onChange(items.filter((_, j) => j !== i))}
                        >
                            ✕
                        </button>
                    </div>
                ))}
                <button
                    type="button"
                    className="tb-btn"
                    onClick={() =>
                        onChange([
                            ...items,
                            keyOptions
                                ? { [keyField]: keyOptions[0]!, [valueField]: "" }
                                : { [keyField]: "Link", [valueField]: "" },
                        ])
                    }
                >
                    + Add link
                </button>
            </div>
        </Field>
    );
}
